import React, { useEffect, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import {
  useGetModelQuery,
  useStopTrainModelMutation,
  useCreateModelMutation,
} from '../../../services/modelsApi'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { setTrainingSource, setValidationSource } from '../../../store/slices/datasetSlice'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import { ViewContainer } from './ModelTrainingView.styles'
import { useGetProjectQuery } from '../../../services/projectsApi'
import DataSourcesCard from './DataSourcesCard'
import BBoxTrainingProgressCard from './BBoxTrainingProgressCard'
import ClassificationTrainingProgressCard from './ClassificationTrainingProgressCard'
import ModelGenerationView from '../ModelGenerationView/ModelGenerationView'
import ModelHeader from '../ModelHeader'
import NoModelsCard from '../ModelsOverview/components/NoModelsCard'
import NewModelModal from '../../../components/modals/NewModelModal'
import { adjectives, nouns } from '../../../utils/modelNameWords'
import { useNavigate } from 'react-router-dom'

const ModelTrainingView: React.FC = () => {
  const dispatch = useAppDispatch()
  const projectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const [isModelTraining, setIsModelTraining] = useState(false)

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: isModelTraining ? 5000 : 60000,
  })

  useEffect(() => {
    if (selectedModel?.state?.toLowerCase() === 'training') {
      setIsModelTraining(true)
    } else {
      setIsModelTraining(false)
    }
  }, [selectedModel?.state])

  const [stopTrainModel] = useStopTrainModelMutation()
  const [createModel] = useCreateModelMutation()

  const [showStopTrainingDialog, setShowStopTrainingDialog] = useState(false)
  const [showNewModelModal, setShowNewModelModal] = useState(false)
  const [selectedBaseModel, setSelectedBaseModel] = useState<{
    id: string
    name: string
  } | null>(null)
  const [filterText, setFilterText] = useState('')

  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const showModelGenerationView = ['unknown', 'initialized', 'stopping', 'err'].includes(
    selectedModel?.state.toLowerCase() || ''
  )

  const baseModels = [{ id: 'baseModel1', name: 'AG Base', image: '/agileview-eye.png' }]

  const gradients = React.useMemo(
    () => ({
      info: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #114ee0)`,
      sources: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #2042ba)`,
      stats: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #2448cc)`,
      settings: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #114ee0)`,
      train: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #2042ba)`,
    }),
    []
  )

  const navigate = useNavigate()

  useEffect(() => {
    dispatch(setTrainingSource(selectedModel?.parameters?.train_metadata?.datasource || []))
    dispatch(setValidationSource(selectedModel?.parameters?.validation_metadata?.datasource || []))
  }, [
    dispatch,
    selectedModel?.id,
    selectedModel?.parameters?.train_metadata?.datasource,
    selectedModel?.parameters?.validation_metadata?.datasource,
  ])

  const generateModelName = () => {
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    return `${randomAdjective}-${randomNoun}`
  }

  const handleGenerateNewModel = async () => {
    if (selectedBaseModel && projectId) {
      try {
        const newModelName = generateModelName()
        await createModel({
          project_id: projectId,
          name: newModelName,
          description: '',
        }).unwrap()

        navigate(`/models/${projectId}/training`)

        setShowNewModelModal(false)
        setSelectedBaseModel(null)
        setFilterText('')
      } catch (err: any) {
        const message = err?.data?.message || 'Failed to create new model'
        setErrorMessage(message)
        setShowError(true)
      }
    }
  }

  const handleCreateModel = () => {
    setShowNewModelModal(true)
  }

  const handleStopTraining = async () => {
    if (selectedModel?.id) {
      try {
        await stopTrainModel({ id: selectedModel.id }).unwrap()
        setShowStopTrainingDialog(false)
      } catch (err) {
        setErrorMessage(
          err
            ? (err as any).data?.message || 'Failed to stop model training'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  if (!selectedModel) {
    return (
      <ViewContainer>
        <NoModelsCard onCreateModel={handleCreateModel} />
        <NewModelModal
          show={showNewModelModal}
          onClose={() => {
            setShowNewModelModal(false)
            setSelectedBaseModel(null)
            setFilterText('')
          }}
          baseModels={baseModels}
          filterText={filterText}
          setFilterText={setFilterText}
          selectedBaseModel={selectedBaseModel}
          onBaseModelSelect={setSelectedBaseModel}
          onGenerateNewModel={handleGenerateNewModel}
        />
        <ErrorDialog
          show={!!errorMessage}
          onHide={() => setErrorMessage(null)}
          message={errorMessage || ''}
        />
      </ViewContainer>
    )
  }

  return (
    <ViewContainer>
      <div className="model-metrics">
        {selectedModel && <ModelHeader />}
        <Row className="g-4">
          {showModelGenerationView ? (
            <ModelGenerationView />
          ) : (
            <>
              <Col md={4}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <DataSourcesCard model={selectedModel} gradient={gradients.sources} />
                </div>
              </Col>

              {project?.annotation_type === 'classification' ? (
                <Col md={8}>
                  <ClassificationTrainingProgressCard
                    model={selectedModel}
                    gradient={gradients.stats}
                  />
                </Col>
              ) : (
                <Col md={8}>
                  <BBoxTrainingProgressCard model={selectedModel} gradient={gradients.stats} />
                </Col>
              )}
            </>
          )}
        </Row>
      </div>

      <ConfirmDialog
        show={showStopTrainingDialog}
        onHide={() => setShowStopTrainingDialog(false)}
        onConfirmRight={handleStopTraining}
        title="Stop Training"
        message="Are you sure you want to stop the model training? This action cannot be undone."
        confirmRightText="Stop Training"
        cancelText="Cancel"
        variant="danger"
      />

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </ViewContainer>
  )
}

export default ModelTrainingView
